import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fiscalDate, formatMoney } from '@makila/fiscal';
import {
  addWorkItem, answerQuote, billableItems, cancelItem, cancelWorkOrder, deliverWithoutCharge, editWork, emitProforma, newWorkOrder, nextWorkSeq, normalizeSerial,
  quoteOutdated, readyMessage, recordQuote, saveOrder, setWorkStatus, warrantyEnd, WORK_LABEL, workLate, workLines, workTotal,
  type CatalogItem, type Customer, type LicenceStatus, type Order, type Signer, type WorkInfo, type WorkStatus,
} from '@makila/pos-core';
import { Camera, Check, ClipboardList, LayoutDashboard, MessageCircle, Moon, Plug, Plus, Printer, RefreshCw, Search, ShoppingCart, Sun, Undo2, Wallet, Wrench, X } from 'lucide-react';
import clsx from 'clsx';
import { CustomerDialog } from '../components/CustomerDialog';
import { HardwareDialog } from '../components/HardwareDialog';
import { Rail, RailItem, RailTool } from '../components/Rail';
import { StatusPill } from '../components/StatusPill';
import { type DeviceState, hasModule, identity, terminalApi } from '../lib/device';
import { printThermal } from '../lib/hw/printer';
import type { IdbTerminalStore } from '../lib/idb-store';
import { receiptHtml } from '../lib/receipt';
import type { SyncStatus } from '../lib/sync';
import { useTheme } from '../lib/theme';
import { workSlipHtml, workTagsHtml } from '../lib/workorder-print';
import type { Operator } from './Lock';
import type { Product } from './Sale';

type Item = Product & { serialRequired?: boolean; warrantyMonths?: number | null };
interface Tech { id: string; name: string; active: boolean; performsServices?: boolean }
const data = <T,>(i: CatalogItem) => i.data as unknown as T;
const dmy = (d: string) => d.split('-').reverse().join('/');
const waLink = (n: string, text: string) => `https://wa.me/${n.replace(/\D/g, '').replace(/^(8\d{8})$/, '258$1')}?text=${encodeURIComponent(text)}`;
const COLUMNS: WorkStatus[] = ['received', 'in_progress', 'ready'];

/** Como se chama o identificador neste negócio. */
const identifierLabel = (type: string) => (type === 'garage' || type === 'carwash' ? 'Matrícula' : type === 'repair' || type === 'electronics' ? 'IMEI ou n.º de série' : 'N.º de série ou matrícula');

interface Props {
  store: IdbTerminalStore; device: DeviceState; signer: Signer; licence: LicenceStatus | null; sync: SyncStatus | null; operator: Operator;
  /** "Entregar e cobrar": a venda abre com o que a ordem tem por cobrar. */
  onCharge: (o: Order) => void;
  onCounter: (open?: 'cash' | 'return') => void; onLock: () => void; onSyncNow: () => void; onGestao?: () => void;
}

/**
 * Ordens de serviço (docs/14 Fase 5): recepção do que o cliente deixa,
 * quadro por estado, orçamento (pró-forma), pronta → aviso por WhatsApp,
 * entregar e cobrar. A ordem é uma comanda: sincroniza entre caixas e
 * funciona sem rede (as fotografias e a garantia precisam de rede).
 */
export function WorkOrdersScreen(p: Props) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Item[]>([]);
  const [techs, setTechs] = useState<Tech[]>([]);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hwOpen, setHwOpen] = useState(false);
  const [dark, toggleTheme] = useTheme();
  const who = identity(p.device);
  const prefix = `${who.storeCode}${who.terminalCode}`;
  const type = p.device.company.businessType;
  const laundry = type === 'laundry';

  const reload = useCallback(async () => {
    setOrders((await p.store.allOrders()).filter((o) => o.kind === 'workorder'));
    setProducts((await p.store.catalog('product')).map(data<Item>).filter((x) => x.active && x.kind !== 'container').sort((a, b) => a.name.localeCompare(b.name, 'pt')));
    const ops = (await p.store.catalog('operator')).map(data<Tech>).filter((o) => o.active);
    setTechs(ops.some((o) => o.performsServices) ? ops.filter((o) => o.performsServices) : ops);
  }, [p.store]);
  useEffect(() => { void reload(); }, [reload, p.sync?.lastSyncAt]);

  const save = async (o: Order) => {
    try { const r = await saveOrder(p.store, who, o); p.onSyncNow(); await reload(); setError(null); return r; } catch (e) { setError((e as Error).message); throw e; }
  };
  const today = fiscalDate();
  const open = orders.filter((o) => o.status === 'open');
  const s = q.trim().toLowerCase();
  const shown = open.filter((o) => !s || [o.work!.number, o.work!.customer.name, o.work!.customer.phone, o.work!.object, o.work!.identifier].some((v) => v?.toLowerCase().includes(s)));
  const current = orders.find((o) => o.id === openId) ?? null;

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (document.querySelector('dialog[open]')) return;
      if (e.key === 'F3' || e.key === '+') { e.preventDefault(); setAdding(true); }
      if (e.key === 'F8' && p.licence?.sell) { e.preventDefault(); p.onCounter('return'); }
      if (e.key === 'F9') { e.preventDefault(); p.onCounter('cash'); }
      if (e.key === 'F10' && p.onGestao) { e.preventDefault(); p.onGestao(); }
    };
    addEventListener('keydown', on);
    return () => removeEventListener('keydown', on);
  });

  const card = (o: Order) => {
    const w = o.work!;
    const late = workLate(o, today);
    const total = workTotal(o);
    return (
      <button key={o.id} onClick={() => setOpenId(o.id)} aria-label={`Ordem ${w.number} ${w.customer.name}`}
        className={clsx('w-full rounded-2xl border-2 bg-[var(--surface)] p-3 text-left shadow-xs', late ? 'border-brand-700' : w.status === 'ready' ? 'border-green-600' : 'border-base')}>
        <div className="flex items-start justify-between gap-2">
          <span className="num font-mono text-sm font-bold">{w.number}</span>
          {w.promisedOn && <span className={clsx('num text-xs', late ? 'font-bold text-brand-700' : 'text-muted')}>{late ? 'Atrasada · ' : ''}{dmy(w.promisedOn)}</span>}
        </div>
        <div className="mt-1 truncate font-semibold">{w.customer.name}</div>
        <div className="truncate text-sm">{w.object}{w.pieces ? ` · ${w.pieces} peça(s)` : ''}</div>
        {w.identifier && <div className="truncate font-mono text-xs text-muted">{w.identifier}</div>}
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
          {total !== '0.00' && <span className="num rounded-full bg-surface-2 px-2 py-0.5 font-semibold">{formatMoney(total)} MT</span>}
          {w.quote && <span className={clsx('rounded-full px-2 py-0.5 font-semibold', w.quote.approvedAt ? 'bg-green-600 text-white' : w.quote.refusedAt ? 'bg-brand-700 text-white' : 'bg-amber-400 text-black')}>
            Orçamento {w.quote.approvedAt ? 'aprovado' : w.quote.refusedAt ? 'recusado' : 'por responder'}</span>}
          {w.warranty?.until && w.warranty.until >= today && <span className="rounded-full bg-sky-600 px-2 py-0.5 font-semibold text-white">Na garantia</span>}
        </div>
      </button>
    );
  };

  return (
    <div className="flex h-full flex-col-reverse md:flex-row">
      <Rail label="Caixa" footer={<>
        {p.onGestao && <RailItem icon={LayoutDashboard} label="Gestão" kbd="F10" ariaLabel="Abrir a gestão" onClick={p.onGestao} />}
        <RailTool icon={Plug} label="Equipamento" onClick={() => setHwOpen(true)} />
        <RailTool icon={dark ? Sun : Moon} label={dark ? 'Modo claro' : 'Modo escuro'} onClick={toggleTheme} />
        <RailTool icon={RefreshCw} label="Sincronizar agora" onClick={p.onSyncNow} />
        <RailTool label="Trocar de operador" onClick={p.onLock}>
          <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-700 text-sm font-bold text-white">{p.operator.name.slice(0, 1).toUpperCase()}</span>
        </RailTool>
      </>}>
        <RailItem icon={ClipboardList} label="Ordens" active badge={open.filter((o) => o.work?.status === 'ready').length || null} />
        <RailItem icon={ShoppingCart} label="Vender" onClick={() => p.onCounter()} />
        <RailItem icon={Undo2} label="Devolução" kbd="F8" disabled={!p.licence?.sell} onClick={() => p.onCounter('return')} />
        <RailItem icon={Wallet} label="Caixa" kbd="F9" onClick={() => p.onCounter('cash')} />
      </Rail>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex min-h-16 flex-wrap items-center gap-x-4 gap-y-2 border-b border-base bg-[var(--surface)] px-4 py-2">
          <div className="min-w-0">
            <div className="truncate font-display text-[17px] font-semibold leading-tight">Ordens de serviço</div>
            <div className="truncate text-xs text-muted">{open.length} abertas · {open.filter((o) => o.work?.status === 'ready').length} prontas · {open.filter((o) => workLate(o, today)).length} atrasadas</div>
          </div>
          <div className="relative min-w-48 flex-1 sm:max-w-sm">
            <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input aria-label="Procurar ordem" placeholder="Número, cliente, telefone, matrícula…" value={q} onChange={(e) => setQ(e.target.value)}
              className="h-12 w-full rounded-xl border border-base bg-surface-2 pl-10 pr-3" />
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <StatusPill sync={p.sync} />
            <button onClick={() => setAdding(true)} title="Nova ordem (F3)" className="flex h-12 items-center gap-2 rounded-xl bg-brand-700 px-4 font-bold text-white"><Plus size={18} /> Nova ordem</button>
          </div>
        </header>
        {error && <div role="alert" className="flex items-center gap-2 bg-amber-100 px-4 py-2 text-amber-900"><span className="flex-1">{error}</span><button aria-label="Fechar aviso" onClick={() => setError(null)} className="grid h-10 w-10 place-items-center"><X size={18} /></button></div>}
        <main className="min-h-0 flex-1 overflow-auto p-4">
          <div className="grid gap-4 md:grid-cols-3">
            {COLUMNS.map((st) => {
              const list = shown.filter((o) => o.work!.status === st).sort((a, b) => (a.work!.promisedOn ?? '9999').localeCompare(b.work!.promisedOn ?? '9999') || a.work!.seq - b.work!.seq);
              return (
                <section key={st} aria-label={WORK_LABEL[st]} className="flex min-h-0 flex-col rounded-2xl border border-base bg-black/[.025] p-3 dark:bg-white/[.04]">
                  <div className="mb-3 flex items-baseline justify-between px-1"><span className="font-semibold">{st === 'received' ? 'Recebidas' : st === 'in_progress' ? 'Em curso' : 'Prontas'}</span><span className="num text-sm text-muted">{list.length}</span></div>
                  <div className="space-y-2.5">
                    {list.map(card)}
                    {!list.length && <p className="px-1 py-6 text-center text-sm text-muted">Nenhuma</p>}
                  </div>
                </section>
              );
            })}
          </div>
        </main>
      </div>

      {adding && (
        <NewWorkDialog store={p.store} device={p.device} techs={techs} services={products.filter((x) => x.kind === 'service')} laundry={laundry} idLabel={identifierLabel(type)}
          number={`${prefix}-${nextWorkSeq(orders, prefix)}`} operatorId={p.operator.id}
          onClose={() => setAdding(false)}
          onCreate={async (w) => {
            const o = newWorkOrder({ id: crypto.randomUUID(), prefix, seq: nextWorkSeq(orders, prefix), ...w, operatorId: p.operator.id, terminalId: who.terminalId, now: new Date() });
            const saved = await save(o);
            setAdding(false);
            setOpenId(saved.id);
            void (async () => { await printThermal(workSlipHtml(saved, p.device)); if (saved.work!.pieces) await printThermal(workTagsHtml(saved)); })();
          }} />
      )}
      {current && (
        <WorkDialog o={current} {...p} products={products} techs={techs} idLabel={identifierLabel(type)} laundry={laundry}
          onSave={save} onClose={() => setOpenId(null)} onCharge={(o) => { setOpenId(null); p.onCharge(o); }} />
      )}
      {hwOpen && <HardwareDialog onClose={() => setHwOpen(false)} />}
    </div>
  );
}

function Shell(p: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { ref.current?.showModal(); }, []);
  return (
    <dialog ref={ref} onCancel={(e) => { e.preventDefault(); p.onClose(); }} className="m-0 h-full max-h-none w-full max-w-none bg-[var(--surface)] p-0 text-[var(--text)] sm:m-auto sm:h-auto sm:max-h-[92vh] sm:w-[min(760px,96vw)] sm:rounded-2xl">
      <div className="flex h-full flex-col sm:max-h-[92vh]">
        <div className="flex items-center justify-between border-b border-base px-5 py-3">
          <h2 className="flex items-center gap-2 text-xl font-bold"><Wrench size={22} className="text-brand-700" /> {p.title}</h2>
          <button type="button" aria-label="Fechar" onClick={p.onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">{p.children}</div>
        {p.footer && <div className="border-t border-base p-4">{p.footer}</div>}
      </div>
    </dialog>
  );
}

const field = 'h-12 w-full rounded-xl border border-base bg-surface-2 px-4';
const label = 'text-sm font-semibold';

/** Recepção: quem deixa, o quê, o que pede e quando fica pronto. */
function NewWorkDialog(p: {
  store: IdbTerminalStore; device: DeviceState; techs: Tech[]; services: Item[]; laundry: boolean; idLabel: string; number: string; operatorId: string;
  onClose: () => void;
  onCreate: (w: { customer: WorkInfo['customer']; object: string; identifier: string | null; problem: string; promisedOn: string | null; pieces: number | null; technicianId: string | null; warranty: WorkInfo['warranty'];
    services: { itemId: string; productId: string; description: string; unitPrice: string; taxRate: string; priceIncludesTax: boolean; exemptionCode: string | null }[] }) => Promise<void>;
}) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [picking, setPicking] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [object, setObject] = useState('');
  const [ident, setIdent] = useState('');
  const [problem, setProblem] = useState('');
  const [promised, setPromised] = useState('');
  const [pieces, setPieces] = useState('');
  const [tech, setTech] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [warranty, setWarranty] = useState<WorkInfo['warranty'] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const checked = useRef('');
  // Vendido por nós? A garantia vê-se pelo número de série (com rede; sem rede fica por ver).
  const checkWarranty = async () => {
    const v = ident.trim().toUpperCase().replace(/\s+/g, '');
    if (v.length < 4 || checked.current === v || !hasModule(p.device, 'serials')) return;
    checked.current = v;
    try {
      const r = await terminalApi<{ sale: { full_number: string; sold_on: string; warranty_until: string | null; description: string } | null }>(p.device, `/terminal/serials/${encodeURIComponent(v)}`);
      setWarranty(r.sale ? { document: r.sale.full_number, soldOn: r.sale.sold_on, until: r.sale.warranty_until } : null);
      if (r.sale && !object.trim()) setObject(r.sale.description);
    } catch { setWarranty(undefined); }
  };
  useEffect(() => {
    if (!customer?.id) return;
    void p.store.catalog('customer').then((all) => { const c = all.find((x) => x.id === customer.id)?.data; if (c?.phone) setPhone(String(c.phone)); });
  }, [customer, p.store]);
  const today = fiscalDate();
  const ok = (customer || name.trim()) && (phone.trim() || customer) && object.trim() && (!p.laundry || Number(pieces) > 0);
  return (
    <Shell title={`Nova ordem ${p.number}`} onClose={p.onClose} footer={
      <button disabled={!ok || busy} onClick={async () => {
        setBusy(true); setError(null);
        try {
          await p.onCreate({
            customer: customer ? { ...(customer.id ? { id: customer.id } : {}), name: customer.name, phone: phone.trim() || null, nuit: customer.nuit } : { name, phone: phone.trim() || null },
            object, identifier: ident.trim() || null, problem, promisedOn: promised || null, pieces: pieces ? Number(pieces) : null, technicianId: tech, warranty: warranty ?? null,
            services: p.services.filter((x) => chosen.includes(x.id)).map((x) => ({ itemId: crypto.randomUUID(), productId: x.id, description: x.name, unitPrice: x.unitPrice, taxRate: x.taxRate, priceIncludesTax: x.priceIncludesTax, exemptionCode: x.exemptionCode })),
          });
        } catch (e) { setError((e as Error).message); setBusy(false); }
      }} className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-brand-700 text-lg font-bold text-white disabled:opacity-40"><Printer size={20} /> Registar e imprimir o talão</button>
    }>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        {customer
          ? <div className="flex h-12 items-center justify-between rounded-xl bg-surface-2 px-4 font-semibold">{customer.name}<button type="button" aria-label="Tirar o cliente" onClick={() => setCustomer(null)}><X size={16} /></button></div>
          : <input autoFocus aria-label="Nome do cliente" placeholder="Nome do cliente" value={name} onChange={(e) => setName(e.target.value)} className={field} />}
        <button type="button" onClick={() => setPicking(true)} className="h-12 rounded-xl border border-base px-4 font-medium">Cliente da ficha</button>
        <input aria-label="Telefone do cliente" inputMode="tel" placeholder="Telefone (para avisar quando estiver pronta)" value={phone} onChange={(e) => setPhone(e.target.value)} className={clsx(field, 'sm:col-span-2')} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block sm:col-span-2"><span className={label}>O que deixou</span>
          <input aria-label="Objecto" placeholder={p.laundry ? 'Ex.: 5 camisas, 1 fato' : 'Ex.: iPhone 11 preto, Toyota Hilux'} value={object} onChange={(e) => setObject(e.target.value)} className={clsx(field, 'mt-1')} /></label>
        {!p.laundry && (
          <label className="block"><span className={label}>{p.idLabel}</span>
            <input aria-label={p.idLabel} value={ident} onChange={(e) => { setIdent(e.target.value.toUpperCase()); setWarranty(undefined); }} onBlur={() => void checkWarranty()} className={clsx(field, 'mt-1 font-mono')} /></label>
        )}
        {p.laundry && (
          <label className="block"><span className={label}>Peças</span>
            <input aria-label="Peças" inputMode="numeric" value={pieces} onChange={(e) => setPieces(e.target.value.replace(/\D/g, '').slice(0, 3))} className={clsx(field, 'mt-1 num')} /></label>
        )}
        <label className="block"><span className={label}>Pronta a</span>
          <input aria-label="Pronta a" type="date" min={today} value={promised} onChange={(e) => setPromised(e.target.value)} className={clsx(field, 'mt-1')} /></label>
        <label className="block sm:col-span-2"><span className={label}>Pedido do cliente / avaria</span>
          <textarea aria-label="Pedido do cliente" rows={2} value={problem} onChange={(e) => setProblem(e.target.value)} className="mt-1 w-full rounded-xl border border-base bg-surface-2 p-3" /></label>
      </div>
      {warranty !== undefined && (
        <p role="status" className={clsx('rounded-xl p-3 text-sm', warranty ? 'bg-sky-50 text-sky-900 dark:bg-sky-950 dark:text-sky-100' : 'bg-surface-2 text-muted')}>
          {warranty
            ? <>Vendido por nós em {dmy(warranty.soldOn)} ({warranty.document}). {warranty.until ? (warranty.until >= today ? <b>Na garantia até {dmy(warranty.until)}.</b> : `Garantia acabou a ${dmy(warranty.until)}.`) : 'Sem garantia registada.'}</>
            : 'Este número não foi vendido por nós.'}
        </p>
      )}
      {p.techs.length > 1 && (
        <fieldset>
          <legend className={clsx(label, 'mb-2')}>Técnico</legend>
          <div className="flex flex-wrap gap-2">
            <button type="button" aria-pressed={tech === null} onClick={() => setTech(null)} className={clsx('h-12 rounded-full border-2 px-4 font-medium', tech === null ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>Depois</button>
            {p.techs.map((t) => <button key={t.id} type="button" aria-pressed={tech === t.id} onClick={() => setTech(t.id)} className={clsx('h-12 rounded-full border-2 px-4 font-medium', tech === t.id ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>{t.name}</button>)}
          </div>
        </fieldset>
      )}
      {p.services.length > 0 && (
        <fieldset>
          <legend className={clsx(label, 'mb-2')}>Serviços <span className="font-normal text-muted">(pode juntar depois)</span></legend>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {p.services.map((x) => {
              const on = chosen.includes(x.id);
              return (
                <button key={x.id} type="button" aria-pressed={on} onClick={() => setChosen(on ? chosen.filter((c) => c !== x.id) : [...chosen, x.id])}
                  className={clsx('flex min-h-16 flex-col justify-between rounded-xl border-2 p-2.5 text-left text-sm', on ? 'border-brand-700 bg-brand-50/60 dark:bg-white/5' : 'border-base')}>
                  <span className="font-medium leading-tight">{x.shortName || x.name}</span>
                  <span className="num text-xs text-muted">{formatMoney(x.unitPrice)} MT</span>
                </button>
              );
            })}
          </div>
        </fieldset>
      )}
      {error && <p role="alert" className="rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
      {picking && <CustomerDialog store={p.store} who={identity(p.device)} onClose={() => setPicking(false)} onPick={(c) => { setCustomer(c); setPicking(false); }} />}
    </Shell>
  );
}

/** Reduz a fotografia (lado maior 1024 px, JPEG): cabe no limite da nuvem e envia-se depressa. */
async function shrink(file: File): Promise<string> {
  const img = await createImageBitmap(file);
  const k = Math.min(1, 1024 / Math.max(img.width, img.height));
  const c = document.createElement('canvas');
  c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
  c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
  for (const q of [0.75, 0.6, 0.45]) { const d = c.toDataURL('image/jpeg', q); if (d.length <= 390_000) return d; }
  throw new Error('Fotografia demasiado grande');
}

/** A ordem: estado, o que se fez, orçamento, fotografias e a entrega. */
function WorkDialog(p: Props & { o: Order; products: Item[]; techs: Tech[]; idLabel: string; laundry: boolean; onSave: (o: Order) => Promise<Order>; onClose: () => void }) {
  const o = p.o;
  const w = o.work!;
  const closed = o.status !== 'open';
  const [adding, setAdding] = useState(false);
  const [pq, setPq] = useState('');
  const [serialFor, setSerialFor] = useState<Item | null>(null);
  const [serial, setSerial] = useState('');
  const [qty, setQty] = useState('1');
  const [notes, setNotes] = useState(w.notes ?? '');
  const [ending, setEnding] = useState<'free' | 'cancel' | null>(null);
  const [reason, setReason] = useState('');
  const [photos, setPhotos] = useState<{ id: string; data: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const now = () => new Date();
  const items = billableItems(o);
  const total = workTotal(o);
  const company = p.device.company.tradeName ?? p.device.company.legalName;
  const run = async (f: () => Promise<unknown>) => { setBusy(true); setError(null); try { await f(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); } };
  const loadPhotos = useCallback(async () => {
    try { setPhotos((await terminalApi<{ items: { id: string; data: string }[] }>(p.device, `/terminal/workorders/${o.id}/photos`)).items); } catch { setPhotos(null); }
  }, [p.device, o.id]);
  useEffect(() => { void loadPhotos(); }, [loadPhotos]);
  const list = useMemo(() => {
    const s = pq.trim().toLowerCase();
    return p.products.filter((x) => !s || x.name.toLowerCase().includes(s) || x.code.toLowerCase() === s || x.barcode === pq.trim()).slice(0, 40);
  }, [pq, p.products]);
  const addProduct = (x: Item, sn?: string) => run(async () => {
    const qn = x.kind === 'service' || !sn ? qty.replace(',', '.') : '1';
    if (!(Number(qn) > 0)) throw new Error('Quantidade inválida');
    const serialN = sn ? normalizeSerial(sn) : null;
    await p.onSave(addWorkItem(o, {
      itemId: crypto.randomUUID(), productId: x.id, description: x.name, unitPrice: x.unitPrice, taxRate: x.taxRate, priceIncludesTax: x.priceIncludesTax, exemptionCode: x.exemptionCode, quantity: qn,
      ...(serialN ? { serial: serialN, warrantyUntil: warrantyEnd(fiscalDate(), x.warrantyMonths) } : {}),
    }, p.operator.id, now()));
    setSerialFor(null); setSerial(''); setQty('1'); setAdding(false); setPq('');
  });
  const tech = p.techs.find((t) => t.id === w.technicianId);
  const btn = 'flex h-12 items-center justify-center gap-2 rounded-xl px-4 font-semibold disabled:opacity-40';

  return (
    <Shell title={<>Ordem {w.number} <span className="rounded-full bg-surface-2 px-3 py-1 text-sm font-semibold">{WORK_LABEL[w.status]}</span></>} onClose={p.onClose} footer={!closed && (
      <div className="grid gap-2 sm:grid-cols-2">
        {w.status === 'received' && <button disabled={busy} onClick={() => run(() => p.onSave(setWorkStatus(o, 'in_progress', p.operator.id, now())))} className={clsx(btn, 'border-2 border-brand-700 text-brand-700')}>Começar</button>}
        {w.status !== 'ready' && <button disabled={busy} onClick={() => run(() => p.onSave(setWorkStatus(o, 'ready', p.operator.id, now())))} className={clsx(btn, 'border-2 border-green-600 text-green-700 dark:text-green-400')}><Check size={18} /> Pronta</button>}
        {w.status === 'ready' && w.customer.phone && (
          <a target="_blank" rel="noreferrer" href={waLink(w.customer.phone, readyMessage(o, company))} className={clsx(btn, 'border-2 border-green-600 text-green-700 dark:text-green-400')}><MessageCircle size={18} /> Avisar por WhatsApp</a>
        )}
        {items.length > 0
          ? <button disabled={busy || !p.licence?.sell} onClick={() => p.onCharge(o)} className={clsx(btn, 'h-14 bg-brand-700 text-lg text-white sm:col-span-2')}>Entregar e cobrar · {formatMoney(total)} MT</button>
          : <button disabled={busy} onClick={() => setEnding('free')} className={clsx(btn, 'border border-base sm:col-span-2')}>Entregar sem cobrar</button>}
      </div>
    )}>
      <div className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div><span className="text-muted">Cliente:</span> <b>{w.customer.name}</b>{w.customer.phone ? ` · ${w.customer.phone}` : ''}</div>
        <div><span className="text-muted">Recebida:</span> {new Date(w.history[0]?.at ?? o.openedAt).toLocaleString('pt-PT', { timeZone: 'Africa/Maputo', dateStyle: 'short', timeStyle: 'short' })}</div>
        <div><span className="text-muted">Objecto:</span> {w.object}{w.pieces ? ` · ${w.pieces} peça(s)` : ''}</div>
        {w.identifier && <div><span className="text-muted">{p.idLabel}:</span> <span className="font-mono">{w.identifier}</span></div>}
        {w.promisedOn && <div><span className="text-muted">Pronta a:</span> {dmy(w.promisedOn)}</div>}
        {w.warranty && <div><span className="text-muted">Garantia:</span> {w.warranty.until ? `até ${dmy(w.warranty.until)}` : 'sem garantia'} ({w.warranty.document})</div>}
        {w.problem && <div className="sm:col-span-2"><span className="text-muted">Pedido:</span> {w.problem}</div>}
      </div>

      {!closed && p.techs.length > 0 && (
        <label className="block"><span className={label}>Técnico</span>
          <select aria-label="Técnico" value={w.technicianId ?? ''} onChange={(e) => void run(() => p.onSave(editWork(o, { technicianId: e.target.value || null }, now())))} className={clsx(field, 'mt-1')}>
            <option value="">— por escolher —</option>
            {p.techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select></label>
      )}
      {closed && tech && <div className="text-sm"><span className="text-muted">Técnico:</span> {tech.name}</div>}

      <section>
        <div className="mb-2 flex items-center justify-between"><h3 className="font-semibold">Serviços e peças</h3><span className="num font-bold">{formatMoney(total)} MT</span></div>
        <ul className="divide-y divide-[var(--border)] rounded-xl border border-base">
          {items.map((i) => (
            <li key={i.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium">{i.description}</div>
                <div className="num text-xs text-muted">{i.quantity.replace('.', ',')} × {formatMoney(i.unitPrice)}{i.serial ? ` · n.º série ${i.serial}` : ''}{i.warrantyUntil ? ` · garantia até ${dmy(i.warrantyUntil)}` : ''}</div>
              </div>
              {!closed && <button aria-label={`Tirar ${i.description}`} onClick={() => void run(() => p.onSave(cancelItem(o, i.id, { reason: 'Tirado da ordem', by: p.operator.id }, now())))} className="grid h-12 w-12 place-items-center rounded-lg text-muted"><X size={18} /></button>}
            </li>
          ))}
          {!items.length && <li className="px-3 py-4 text-center text-sm text-muted">Ainda nada. Junte o serviço ou as peças.</li>}
        </ul>
        {!closed && !adding && <button onClick={() => setAdding(true)} className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-base font-semibold"><Plus size={18} /> Juntar serviço ou peça</button>}
        {adding && (
          <div className="mt-2 rounded-xl border border-base p-3">
            {serialFor ? (
              <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); void addProduct(serialFor, serial); }}>
                <div className="text-sm font-semibold">{serialFor.name} — número de série da peça</div>
                <input autoFocus aria-label="Número de série da peça" value={serial} onChange={(e) => setSerial(e.target.value.toUpperCase())} className={clsx(field, 'font-mono')} />
                <div className="flex gap-2"><button type="button" onClick={() => setSerialFor(null)} className={clsx(btn, 'border border-base')}>Voltar</button><button disabled={!serial.trim() || busy} className={clsx(btn, 'flex-1 bg-brand-700 text-white')}>Juntar</button></div>
              </form>
            ) : (
              <>
                <div className="flex gap-2">
                  <input autoFocus aria-label="Procurar serviço ou peça" placeholder="Procurar serviço ou peça" value={pq} onChange={(e) => setPq(e.target.value)} className={clsx(field, 'flex-1')} />
                  <input aria-label="Quantidade" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} className={clsx(field, 'num w-24 text-right')} />
                  <button aria-label="Fechar a procura" onClick={() => setAdding(false)} className="grid h-12 w-12 place-items-center rounded-lg border border-base"><X size={18} /></button>
                </div>
                <div className="mt-2 grid max-h-64 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
                  {list.map((x) => (
                    <button key={x.id} disabled={busy} onClick={() => (x.serialRequired ? setSerialFor(x) : void addProduct(x))} className="flex min-h-16 flex-col justify-between rounded-xl border border-base p-2.5 text-left text-sm">
                      <span className="font-medium leading-tight">{x.shortName || x.name}</span>
                      <span className="num text-xs text-muted">{formatMoney(x.unitPrice)} MT{x.kind === 'service' ? '' : ' · peça'}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-2 font-semibold">Orçamento</h3>
        {w.quote ? (
          <div className={clsx('rounded-xl p-3 text-sm', w.quote.approvedAt ? 'bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100' : w.quote.refusedAt ? 'bg-brand-50 text-brand-800' : 'bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200')}>
            Pró-forma {w.quote.number} · <span className="num font-semibold">{formatMoney(w.quote.total)} MT</span> · {w.quote.approvedAt ? 'aprovado pelo cliente' : w.quote.refusedAt ? 'recusado pelo cliente' : 'à espera da resposta do cliente'}
            {quoteOutdated(o) && <div className="mt-1 font-semibold">A ordem mudou depois do orçamento: faça um novo.</div>}
            {!closed && !w.quote.approvedAt && !w.quote.refusedAt && (
              <div className="mt-2 flex gap-2">
                <button disabled={busy} onClick={() => run(() => p.onSave(answerQuote(o, true, p.operator.id, now())))} className={clsx(btn, 'flex-1 bg-green-600 text-white')}>Cliente aprovou</button>
                <button disabled={busy} onClick={() => run(() => p.onSave(answerQuote(o, false, p.operator.id, now())))} className={clsx(btn, 'flex-1 border border-current')}>Recusou</button>
              </div>
            )}
          </div>
        ) : <p className="text-sm text-muted">Sem orçamento.</p>}
        {!closed && items.length > 0 && (!w.quote || quoteOutdated(o)) && (
          <button disabled={busy} onClick={() => run(async () => {
            const pf = await emitProforma(p.store, identity(p.device), p.signer, { lines: workLines(o), customer: { ...(w.customer.id ? { id: w.customer.id } : {}), name: w.customer.name, nuit: w.customer.nuit ?? null }, operatorId: p.operator.id });
            await p.onSave(recordQuote(o, pf, now()));
            void printThermal(receiptHtml(pf, p.device));
          })} className={clsx(btn, 'mt-2 w-full border-2 border-brand-700 text-brand-700')}>{w.quote ? 'Novo orçamento (pró-forma)' : 'Fazer orçamento (pró-forma)'}</button>
        )}
      </section>

      <section>
        <h3 className="mb-2 font-semibold">Notas e diagnóstico</h3>
        <textarea aria-label="Notas" rows={3} disabled={closed} value={notes} onChange={(e) => setNotes(e.target.value)}
          onBlur={() => { if (notes !== (w.notes ?? '')) void run(() => p.onSave(editWork(o, { notes: notes.trim() || null }, now()))); }}
          className="w-full rounded-xl border border-base bg-surface-2 p-3" />
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold">Fotografias</h3>
          {photos !== null && photos.length < 6 && (
            <>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" aria-label="Fotografia" onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void run(async () => { await terminalApi(p.device, `/terminal/workorders/${o.id}/photos`, { method: 'POST', body: JSON.stringify({ data: await shrink(f) }) }); await loadPhotos(); });
              }} />
              <button disabled={busy} onClick={() => fileRef.current?.click()} className={clsx(btn, 'border border-base')}><Camera size={18} /> Tirar</button>
            </>
          )}
        </div>
        {photos === null ? <p className="text-sm text-muted">Sem rede: as fotografias vêem-se e tiram-se com ligação.</p>
          : photos.length ? <div className="grid grid-cols-3 gap-2">{photos.map((f) => <img key={f.id} src={f.data} alt="Fotografia da recepção" className="aspect-square w-full rounded-lg object-cover" />)}</div>
            : <p className="text-sm text-muted">Nenhuma.</p>}
      </section>

      <section>
        <h3 className="mb-2 font-semibold">Histórico</h3>
        <ol className="space-y-1 text-sm">
          {w.history.map((h) => <li key={`${h.status}${h.at}`}><span className="num text-muted">{new Date(h.at).toLocaleString('pt-PT', { timeZone: 'Africa/Maputo', dateStyle: 'short', timeStyle: 'short' })}</span> · {WORK_LABEL[h.status]}</li>)}
          {o.documents.map((d) => <li key={d}>Documento {d}</li>)}
        </ol>
        {w.notes && closed && <p className="mt-2 whitespace-pre-line text-sm text-muted">{w.notes}</p>}
      </section>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => void printThermal(workSlipHtml(o, p.device))} className={clsx(btn, 'border border-base')}><Printer size={18} /> Talão</button>
        {w.pieces ? <button onClick={() => void printThermal(workTagsHtml(o))} className={clsx(btn, 'border border-base')}><Printer size={18} /> Senhas das peças</button> : null}
        {!closed && <button onClick={() => setEnding('cancel')} className={clsx(btn, 'ml-auto text-brand-700')}>Anular a ordem</button>}
      </div>

      {ending && (
        <form className="space-y-2 rounded-xl border-2 border-brand-700 p-3" onSubmit={(e) => {
          e.preventDefault();
          void run(async () => { await p.onSave(ending === 'free' ? deliverWithoutCharge(o, p.operator.id, now(), reason) : cancelWorkOrder(o, p.operator.id, now(), reason)); setEnding(null); p.onClose(); });
        }}>
          <div className="font-semibold">{ending === 'free' ? 'Entregar sem cobrar — porquê?' : 'Anular a ordem — motivo'}</div>
          <input autoFocus aria-label="Motivo" placeholder={ending === 'free' ? 'Ex.: garantia, orçamento recusado' : 'Ex.: cliente desistiu'} value={reason} onChange={(e) => setReason(e.target.value)} className={field} />
          <div className="flex gap-2"><button type="button" onClick={() => setEnding(null)} className={clsx(btn, 'border border-base')}>Voltar</button><button disabled={!reason.trim() || busy} className={clsx(btn, 'flex-1 bg-brand-700 text-white')}>Confirmar</button></div>
        </form>
      )}
      {error && <p role="alert" className="rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
    </Shell>
  );
}
